import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { config } from '@/lib/config';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});
type LoginForm = z.infer<typeof loginSchema>;

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    next: typeof search.next === 'string' ? search.next : undefined,
  }),
  component: LoginPage,
});

const BOOT_LINES: { label: string; value: string }[] = [
  { label: 'api', value: 'ready' },
  { label: 'database', value: 'postgres · ok' },
  { label: 'cache', value: 'redis ok' },
  { label: 'search', value: 'meilisearch ok' },
  { label: 'media', value: 'storage ready' },
];

function LoginPage() {
  const { login } = useAuth();
  const router = useRouter();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  async function onSubmit(values: LoginForm) {
    setSubmitting(true);
    try {
      await login(values.email, values.password);
      toast.success('Sesión iniciada');
      await router.invalidate();
      void navigate({ to: next ?? '/' });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Credenciales inválidas';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2" style={{ background: '#060309' }}>
      <style>{`@keyframes sytelBlink{0%,49%{opacity:1}50%,100%{opacity:0}}.sytel-cursor{animation:sytelBlink 1s steps(1) infinite}`}</style>

      {/* Panel consola (solo desktop) */}
      <aside
        className="relative hidden flex-col overflow-hidden p-10 lg:flex xl:p-14"
        style={{
          background:
            'radial-gradient(130% 130% at 0% 0%, rgba(109,40,217,0.22), transparent 55%), radial-gradient(120% 120% at 100% 100%, rgba(173,2,106,0.22), transparent 50%), #07040d',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
          }}
        />

        <div className="relative flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
          <span>sytel/cms · console</span>
          <span className="flex items-center gap-2 text-emerald-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.6)]" />
            online
          </span>
        </div>

        <div className="relative flex flex-1 flex-col items-center justify-center py-10 text-center">
          {config.brandLogoUrl ? (
            <img src={config.brandLogoUrl} alt={config.brandName} className="h-24 w-auto xl:h-28" />
          ) : (
            <span className="text-4xl font-bold tracking-tight text-white xl:text-5xl">
              {config.brandName}
            </span>
          )}
          <p className="mt-6 font-mono text-sm uppercase tracking-[0.34em] text-neutral-500">
            Content Management
            <span className="mx-2 text-[#AD026A]">/</span>
            console
          </p>

          <div
            className="mt-10 w-full max-w-lg rounded-xl p-6 text-left font-mono text-sm leading-relaxed"
            style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="text-neutral-400">
              <span className="text-[#F2B8DC]">&gt;</span> cms login{' '}
              <span className="text-neutral-600">--scope=editor</span>
            </div>
            <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-wider text-neutral-500">
              <span>boot · console</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {BOOT_LINES.map((line) => (
                <div key={line.label} className="flex items-center gap-3 text-neutral-300">
                  <span className="text-emerald-400">✓</span>
                  <span className="w-20 text-neutral-500">{line.label}</span>
                  <span className="text-neutral-600">·</span>
                  <span>{line.value}</span>
                </div>
              ))}
              <div className="flex items-center gap-3 text-neutral-300">
                <span className="text-[#AD026A]">→</span>
                <span className="w-20 text-neutral-500">session</span>
                <span className="text-neutral-600">·</span>
                <span>
                  awaiting credentials
                  <span className="sytel-cursor ml-0.5 inline-block h-3.5 w-2 translate-y-0.5 bg-[#F2B8DC]" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-600">
          <div className="text-neutral-700">{config.brandName} · CMS · USO INTERNO</div>
        </div>
      </aside>

      {/* Panel formulario */}
      <main className="relative flex items-center justify-center p-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background:
              'radial-gradient(120% 120% at 0% 0%, rgba(109,40,217,0.22), transparent 55%), radial-gradient(120% 120% at 100% 100%, rgba(173,2,106,0.24), transparent 50%)',
          }}
        />

        <div className="relative w-full max-w-sm">
          {config.brandLogoUrl ? (
            <img src={config.brandLogoUrl} alt={config.brandName} className="mb-8 h-12 w-auto lg:hidden" />
          ) : (
            <span className="mb-8 block text-2xl font-bold text-white lg:hidden">{config.brandName}</span>
          )}

          <header className="mb-6 space-y-1">
            <h1 className="text-2xl font-bold text-white">Iniciar sesión</h1>
            <p className="text-sm text-neutral-400">Introduce tus credenciales para acceder.</p>
          </header>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-neutral-300">
                Email
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
                  <MailIcon />
                </span>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="tu@email.com"
                  className="w-full rounded-lg border border-white/12 bg-white/[0.04] py-2.5 pl-10 pr-3 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#AD026A] focus:bg-white/[0.06]"
                  {...register('email', { required: true })}
                />
              </div>
              {errors.email && <p className="text-xs text-[#ff8da7]">Email no válido</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-neutral-300">
                Contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
                  <LockIcon />
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-white/12 bg-white/[0.04] py-2.5 pl-10 pr-10 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#AD026A] focus:bg-white/[0.06]"
                  {...register('password', { required: true, minLength: 6 })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors hover:text-neutral-300"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-[#ff8da7]">Mínimo 6 caracteres</p>}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-white shadow-lg shadow-[#AD026A]/20 transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg, #66023C 0%, #AD026A 100%)' }}
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              )}
              {submitting ? 'Entrando…' : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-neutral-600">
            Acceso restringido a editores autorizados · © {new Date().getFullYear()} {config.brandName}
          </p>
        </div>
      </main>
    </div>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.36M6.6 6.6A13.3 13.3 0 0 0 2 11s3.5 7 10 7a9 9 0 0 0 4.2-1M3 3l18 18" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
